import {needsServerMessageHydration} from './campaignMessageHydration';

function normalizeStoredMessage(msg, index) {
  if (msg.parts && Array.isArray(msg.parts)) {
    return {
      ...msg,
      id: msg.id || `stored-${index}`,
    };
  }
  return {
    id: msg.id || `stored-${index}`,
    role: msg.role,
    parts: [{type: 'text', text: msg.content || ''}],
  };
}

function mapStoredMessages(messages = []) {
  return messages.map(normalizeStoredMessage);
}

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

function normalizeComparableValue(value) {
  if (value == null) {
    return null;
  }
  if (typeof value !== 'object') {
    return value;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
}

function normalizeToolInput(input) {
  if (input == null) {
    return null;
  }
  if (typeof input === 'object' && !Array.isArray(input)) {
    const keys = Object.keys(input);
    if (keys.length === 0) {
      return null;
    }
  }
  return normalizeComparableValue(input);
}

function buildPartIdentity(part) {
  if (!part) {
    return 'part:null';
  }

  if (part.type === 'text') {
    return `text:${normalizeText(part.text)}`;
  }

  if (part.type === 'file') {
    return `file:${normalizeText(part.mediaType)}:${normalizeText(part.url)}:${normalizeText(part.filename)}`;
  }

  if (part.type?.startsWith('tool-')) {
    const trackingId = part.toolCallId || part.output?.trackingId || '';
    const inputIdentity = normalizeToolInput(part.input);
    return `tool:${part.type}:${trackingId}:${JSON.stringify(inputIdentity)}`;
  }

  return `other:${normalizeText(part.type)}`;
}

function rolesAlignByPosition(localMessages = [], serverMessages = []) {
  const length = Math.min(localMessages.length, serverMessages.length);
  if (length === 0) {
    return false;
  }

  for (let index = 0; index < length; index += 1) {
    if (localMessages[index]?.role !== serverMessages[index]?.role) {
      return false;
    }
  }

  return true;
}

function reconcileAlignedTranscript(localMessages = [], serverMessages = []) {
  const sharedLength = Math.min(localMessages.length, serverMessages.length);
  const mergedPrefix = [];

  for (let index = 0; index < sharedLength; index += 1) {
    if (localMessages[index]?.role !== serverMessages[index]?.role) {
      break;
    }
    mergedPrefix.push(
      mergeMessageById(localMessages[index], serverMessages[index]),
    );
  }

  if (mergedPrefix.length === 0) {
    return null;
  }

  if (mergedPrefix.length === localMessages.length) {
    return [
      ...mergedPrefix,
      ...serverMessages.slice(mergedPrefix.length),
    ];
  }

  if (mergedPrefix.length === serverMessages.length) {
    return [
      ...mergedPrefix,
      ...localMessages.slice(mergedPrefix.length),
    ];
  }

  return [
    ...mergedPrefix,
    ...localMessages.slice(mergedPrefix.length),
    ...serverMessages.slice(mergedPrefix.length),
  ];
}

function buildMessageSignature(message) {
  return JSON.stringify({
    role: message?.role ?? '',
    parts: (message?.parts || []).map(buildPartIdentity),
  });
}

function mergeToolParts(localParts = [], serverParts = []) {
  const serverByType = new Map();
  serverParts.forEach((part) => {
    if (part.type?.startsWith('tool-')) {
      const key = part.toolCallId || part.type;
      serverByType.set(key, part);
    }
  });

  const merged = localParts.map((part) => {
    if (!part.type?.startsWith('tool-')) {
      return part;
    }
    const key = part.toolCallId || part.type;
    const serverPart = serverByType.get(key);
    if (!serverPart) {
      return part;
    }
    return {
      ...part,
      ...serverPart,
      output: serverPart.output ?? part.output,
      state: serverPart.state ?? part.state,
    };
  });

  serverParts.forEach((part) => {
    if (!part.type?.startsWith('tool-')) {
      return;
    }
    const key = part.toolCallId || part.type;
    const exists = merged.some(
      (localPart) =>
        localPart.type?.startsWith('tool-') &&
        (localPart.toolCallId || localPart.type) === key,
    );
    if (!exists) {
      merged.push(part);
    }
  });

  return merged;
}

function mergeMessageById(localMessage, serverMessage) {
  if (!localMessage || !serverMessage) {
    return serverMessage ?? localMessage;
  }

  return {
    ...localMessage,
    ...serverMessage,
    id: localMessage.id ?? serverMessage.id,
    parts: mergeToolParts(localMessage.parts, serverMessage.parts),
  };
}

function buildMessageSignatureQueues(messages = []) {
  const queues = new Map();

  messages.forEach((message, position) => {
    const signature = buildMessageSignature(message);
    const queue = queues.get(signature) || [];
    queue.push({message, position});
    queues.set(signature, queue);
  });

  return queues;
}

/**
 * Reconciles persisted server messages into the local transcript without
 * disrupting in-flight turns or reordering confirmed local messages.
 */
export function reconcileCampaignTranscript(
  localMessages = [],
  serverMessages = [],
  options = {},
) {
  const {
    allowFullReplace = false,
    chatStatus = 'ready',
  } = options;

  if (chatStatus === 'submitted' || chatStatus === 'streaming') {
    return localMessages;
  }

  const normalizedServer = mapStoredMessages(serverMessages);

  if (allowFullReplace || !localMessages.length) {
    return normalizedServer;
  }

  if (needsServerMessageHydration(normalizedServer, localMessages)) {
    return normalizedServer;
  }

  if (rolesAlignByPosition(localMessages, normalizedServer)) {
    const aligned = reconcileAlignedTranscript(localMessages, normalizedServer);
    if (aligned) {
      return aligned;
    }
  }

  const serverQueues = buildMessageSignatureQueues(normalizedServer);
  const consumedServerPositions = new Set();
  const reconciled = localMessages.map((localMessage) => {
    const signature = buildMessageSignature(localMessage);
    const queue = serverQueues.get(signature);
    const serverEntry = queue && queue.length > 0 ? queue.shift() : null;
    if (!serverEntry) {
      return localMessage;
    }
    consumedServerPositions.add(serverEntry.position);
    return mergeMessageById(localMessage, serverEntry.message);
  });

  const appended = normalizedServer.filter(
    (_serverMessage, index) => !consumedServerPositions.has(index),
  );

  const merged = [...reconciled, ...appended];
  const reconciledSignatures = new Set(
    reconciled.map((message) => buildMessageSignature(message)),
  );

  const dedupedAppend = appended.filter(
    (serverMessage) => !reconciledSignatures.has(buildMessageSignature(serverMessage)),
  );

  return dedupedAppend.length === appended.length
    ? merged
    : [...reconciled, ...dedupedAppend];
}

export {mapStoredMessages};
